// Types
import { LineType, LineVariationKeys, Point, VictoryLine, VictoryLines } from '@organisms/index';
import { RatingKeys, RatingRanges } from '@globalTypes/index';
// Utils
import { findRangeKey } from '@helpers/index';

/**
 * Transforms an array of data points into an array of VictoryLines, grouping points based on rating ranges.
 *
 * @function
 * @param line - An array of data points represented as Point[], where each point has x and y coordinates.
 * @param ranges - An object representing rating ranges, used to group data points into lines based on their y-coordinate values.
 * @param title - A string representing the title of the VictoryLines.
 * @param styleComplement - An optional parameter that specifies the line style, either LineType.Dashed or an empty string ('').
 *                          Default value is an empty string.
 * @returns An array of VictoryLines, where each VictoryLine represents a group of data points falling within a specific rating range.
 */
export const transformLinesIntoRatedLines = (
  line: Point[],
  ranges: RatingRanges,
  title: string,
  name: string,
  styleComplement: LineType.Dashed | '' = ''
): VictoryLines => {
  // Initialize previous range key and an empty array to store grouped data.
  let prevRangeKey: string | undefined;
  const groupedData: VictoryLines = [];

  // Loop through each data point in the 'line' array.
  for (const { y, x } of line) {
    // Find the corresponding range key for the current 'y' value using the 'ranges' object.
    const range = findRangeKey(Number(y), ranges);
    // Get the last grouped data line from the 'groupedData' array.
    const currentLine = groupedData[groupedData.length - 1];

    if (range && range === prevRangeKey) {
      // If the current range is the same as the previous range, add the data point to the existing line.
      currentLine.data.push({ x, y });
    } else {
      if (currentLine) {
        // If the current range is different from the previous range and there is a 'currentLine', it means
        // we need to close the existing line and create a new line.
        const previousRange = currentLine.styleKey as RatingKeys;

        // Close the existing line by adding the last data point from the 'currentLine' to the new line's data.
        groupedData.push({
          title,
          data: [{ x, y }, currentLine.data[currentLine.data.length - 1]],
          styleKey: `${previousRange}to${range}${styleComplement}` as LineVariationKeys,
          id: name,
        });
      }

      // If the current range is different from the previous range, create a new line and add it to 'groupedData'.
      if (range) {
        prevRangeKey = range;
        const newLine: VictoryLine = {
          title: title,
          data: [{ x, y }],
          styleKey: `${range}${styleComplement}`,
          id: name,
        };
        groupedData.push(newLine);
      }
    }
  }

  // Return the array of grouped data points as VictoryLines.
  return groupedData;
};
